import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import {
	CreatePlan,
	ItemPopover,
	ItemPopoverService,
	OfferTypeChooser,
	PaginatedResponse,
	Plan,
	PlanResponse,
	PopoverResponseMap,
	SearchAdvancedPlans
} from '@interfaces'
import { BehaviorSubject, map, Observable } from 'rxjs'
import { buildConfigQueryParams } from './config-query-params.helper'
import { hasConfigTextSearchMinLength } from './config-text-search.helper'
import { normalizeRemoteSearchStringParam } from '../remote-text-search.helper'

export interface PlansFilters {
	active?: boolean
	searchString?: string
	name?: string
	countryId?: number
	countryName?: string
	continentId?: number
	continentName?: string
	amountMin?: number
	amountMax?: number
	amountExact?: number
}

export const PLAN_SORT_FIELDS = ['createDate', 'name', 'price'] as const
export const PLAN_SORT_DIRECTIONS = ['asc', 'desc'] as const

export type PlanSortField = (typeof PLAN_SORT_FIELDS)[number]
export type PlanSortDirection = (typeof PLAN_SORT_DIRECTIONS)[number]

export interface PlanSort {
	sortField?: PlanSortField | string | null
	sortDirection?: PlanSortDirection | string | null
}

export interface NormalizedPlanSort {
	sortField: PlanSortField
	sortDirection: PlanSortDirection
}

export const DEFAULT_PLAN_SORT: NormalizedPlanSort = {
	sortField: 'createDate',
	sortDirection: 'desc'
}

const DEFAULT_PLAN_PICKER_SORT: NormalizedPlanSort = {
	sortField: 'name',
	sortDirection: 'asc'
}

const isPlanSortField = (value: string): value is PlanSortField =>
	(PLAN_SORT_FIELDS as readonly string[]).includes(value)
const isPlanSortDirection = (value: string): value is PlanSortDirection =>
	(PLAN_SORT_DIRECTIONS as readonly string[]).includes(value)
const normalizePlanSortField = (value: string): PlanSortField | null => {
	if (value === 'createdate' || value === 'create_date') return 'createDate'
	if (isPlanSortField(value)) return value
	return null
}

export const normalizePlanSort = (
	sort: PlanSort = {},
	defaultSort: NormalizedPlanSort = DEFAULT_PLAN_SORT
): NormalizedPlanSort => {
	const normalizedField = String(sort.sortField ?? '')
		.trim()
		.toLowerCase()
	const normalizedDirection = String(sort.sortDirection ?? '')
		.trim()
		.toLowerCase()
	const sortField = normalizePlanSortField(normalizedField)

	return {
		sortField: sortField ?? defaultSort.sortField,
		sortDirection: isPlanSortDirection(normalizedDirection) ? normalizedDirection : defaultSort.sortDirection
	}
}

export type PlanPickerFlowContext = 'create' | 'activate' | 'change'

export interface PlanPickerRequest {
	flowContext: PlanPickerFlowContext
	subscriberId?: number
	countryCode?: string
	isEnterprise?: boolean
	lang?: string
	search?: string
	page?: number
	size?: number
	sortField?: PlanSortField | string | null
	sortDirection?: PlanSortDirection | string | null
}

export interface PlanPickerItem {
	id: number
	name: string
	description: string
	shortDescription: string | null
	price: number
	paymentType: string | null
	subscriptionType: string | null
	promotionLabel: string | null
	countries: string[]
	plan: Plan
}

export type PlanPickerResponse = PaginatedResponse<PlanPickerItem>

const normalizePlanPickerSearch = (value?: string): string | undefined => {
	if (typeof value !== 'string') return undefined

	const normalizedValue = value.trim()
	if (!normalizedValue) return undefined
	if (!hasConfigTextSearchMinLength(normalizedValue)) return undefined

	return normalizedValue
}

const buildPlanPickerQueryParams = (request: PlanPickerRequest) => {
	const normalizedSort = normalizePlanSort(request, DEFAULT_PLAN_PICKER_SORT)
	const baseParams = {
		flowContext: request.flowContext,
		page: request.page ?? 1,
		size: request.size ?? 15,
		lang: request.lang,
		search: normalizePlanPickerSearch(request.search),
		...normalizedSort
	}

	if (request.flowContext === 'change') {
		return buildConfigQueryParams({
			...baseParams,
			subscriberId: request.subscriberId
		})
	}

	return buildConfigQueryParams({
		...baseParams,
		subscriberId: request.subscriberId,
		countryCode: request.countryCode,
		isEnterprise: request.isEnterprise ?? false
	})
}

const getInPlanOffer = (plan: Plan) =>
	plan.offerPlans?.find(({ offer }) => {
		if (!offer) return false
		if (offer.offerTypeChooser !== OfferTypeChooser.IN_THE_PLAN) return false

		const chooserValue = typeof offer.chooserValue === 'string' ? offer.chooserValue.trim() : ''
		const codeValue = typeof offer.code === 'string' ? offer.code.trim() : ''

		return chooserValue.length === 0 && codeValue.length === 0
	}) ?? null

export const mapPlanToPickerItem = (plan: Plan): PlanPickerItem => {
	const translation = plan.translations?.[0]
	const inPlanOffer = getInPlanOffer(plan)?.offer
	const countries = (plan.planCountries ?? [])
		.map(planCountry => planCountry.libCountry?.translations?.[0]?.name)
		.filter((countryName): countryName is string => Boolean(countryName))

	return {
		id: plan.id,
		name: translation?.name ?? '',
		description: translation?.description ?? '',
		shortDescription: translation?.shortDescription ?? null,
		price: Number(plan.price),
		paymentType: plan.libPaymentType?.description ?? null,
		subscriptionType: plan.libSubscriptionType?.description ?? null,
		promotionLabel: inPlanOffer?.translations?.[0]?.name ?? null,
		countries,
		plan
	}
}

@Injectable({
	providedIn: 'root'
})
export class PlansService implements ItemPopoverService {
	private http = inject(HttpClient)
	private url = '/plans'
	private readonly textFilterKeys: (keyof PlansFilters)[] = ['searchString', 'name', 'countryName', 'continentName']

	// Observable para la creacion de planes
	private _creatingPlan: boolean = false
	private creatingPlanSubject = new BehaviorSubject<boolean>(false)
	public creatingPlan$ = this.creatingPlanSubject.asObservable()

	// Trae una lista paginada de los planes
	getPlans(page = 1, size = 15, filters: PlansFilters & PlanSort = {}): Observable<PlanResponse> {
		const normalizedSort = normalizePlanSort(filters)
		const normalizedFilters = this.normalizePlanFilters(filters)
		const params = buildConfigQueryParams({
			page,
			size,
			...normalizedFilters,
			...normalizedSort
		})
		return this.http.get<PlanResponse>(this.url, { params })
	}

	getPlanPicker(request: PlanPickerRequest): Observable<PlanPickerResponse> {
		const params = buildPlanPickerQueryParams(request)

		return this.http.get<PlanResponse>(`${this.url}/picker`, { params }).pipe(
			map(response => ({
				...response,
				content: response.content.map(mapPlanToPickerItem)
			}))
		)
	}

	getAdvancedSearchPlans(body: SearchAdvancedPlans): Observable<PlanResponse> {
		return this.http.post<PlanResponse>(`${this.url}/advancedSearch`, body)
	}

	// Trae una lista paginada de los planes
	getFilteredPlans(
		page = 1,
		size = 15,
		active = true,
		filters: Omit<PlansFilters, 'active'> = {}
	): Observable<PlanResponse> {
		return this.getPlans(page, size, { ...filters, active })
	}

	// Crea un nuevo plan
	createPlan(plan: CreatePlan): Observable<Plan> {
		return this.http.post<Plan>(this.url, plan)
	}

	updatePlan(plan: CreatePlan): Observable<Plan> {
		return this.http.put<Plan>(`${this.url}`, plan)
	}

	enableDisablePlans(plans: { id: number }[], active: boolean): Observable<boolean> {
		return this.http.put<boolean>(`${this.url}/status?active=${active}`, plans)
	}

	checkSubscriberAssociated(idPlan: number): Observable<boolean> {
		return this.http.get<boolean>(`${this.url}/subscriberAssociated?idPlan=${idPlan}`)
	}

	// Eliminas los planes especificados
	deletePlans(plans: number[]): Observable<boolean> {
		return this.http.delete<boolean>(`${this.url}`, { body: plans })
	}

	// Alterna el estado global de creación de planes
	togleCreatingPlan() {
		this._creatingPlan = !this._creatingPlan
		this.creatingPlanSubject.next(this._creatingPlan)
	}

	// Marca el estado global de creación de planes como false
	resetCreatingState() {
		this._creatingPlan = false
		this.creatingPlanSubject.next(this._creatingPlan)
	}

	// Obtiene los libSubscriptionType
	getLibSubscriptionTypes() {
		const URL = `${this.url}/subscriptionTypes`
		return this.http.get<any[]>(URL)
	}

	// Recoge los planes de los suscriptores
	getSubscriberPlans(
		forParticular: boolean = true,
		forEnterprise: boolean = false,
		typeUser: string,
		planId?: number | null,
		countryId?: number,
		status?: number
	): Observable<PlanResponse> {
		let URL = `${this.url}/subscription?forParticular=${forParticular}&forEnterprise=${forEnterprise}&countryId=${countryId}&typeUser=${typeUser}`

		if (planId) URL += `&planId=${planId}&status=${status}`

		return this.http.get<PlanResponse>(URL)
	}

	// Obtiene los libPaymentTypes
	getLibPaymentTypes() {
		const URL = `${this.url}/paymentTypes`
		return this.http.get<any[]>(URL)
	}

	getCalculatedId(): Observable<number> {
		return this.http.get<number>(`${this.url}/provisionalCode`)
	}

	getItemPopoverData(
		page: number,
		size: number,
		_type?: string,
		params?: SearchAdvancedPlans
	): Observable<PopoverResponseMap> {
		const body: SearchAdvancedPlans = {
			...params,
			page,
			size,
			active: true,
			includeBar: true,
			searchString: normalizeRemoteSearchStringParam(params?.searchString),
			includeWithActiveOffers: params?.includeWithActiveOffers ?? true,
			lightweight: params?.lightweight ?? true
		}

		return this.getAdvancedSearchPlans(body).pipe(
			map(response => ({
				...response,
				content: response.content.map(
					({ id, translations }): ItemPopover => ({
						id,
						value: translations[0].name,
						active: false
					})
				)
			}))
		)
	}

	private normalizePlanFilters(filters: PlansFilters): PlansFilters {
		const normalizedFilters: PlansFilters = { ...filters }
		const mutableFilters = normalizedFilters as Record<string, unknown>

		for (const key of this.textFilterKeys) {
			const value = filters[key]
			if (typeof value !== 'string') continue

			const normalizedValue = normalizeRemoteSearchStringParam(value)
			if (normalizedValue) {
				mutableFilters[key] = normalizedValue
			} else {
				delete mutableFilters[key]
			}
		}

		return normalizedFilters
	}
}
